// AllDocumentsTable.js - Vue globale documents groupés par joueur

import React, { useEffect, useState, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { VariableSizeList } from 'react-window';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  Grid,
  useTheme,
  useMediaQuery,
  SwipeableDrawer,
  Fab,
  Divider,
  Card,
  CardContent,
  CardHeader,
  Tooltip,
  Autocomplete,
  Pagination,
  TablePagination,
  Chip,
} from '@mui/material';

import { Link } from 'react-router-dom';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

import api from '../api/axiosConfig';
import { getMediaBaseUrl } from '../utils/mediaUrl';
import { canModifyRecord } from '../utils/permissions';
import { usePositions } from '../utils/positions';
import { isImage, isPdf, isVideo, isHtml } from '../utils/documentTypes';
import DocumentMediaDialog from './DocumentMediaDialog';
import DocumentThumbnail from './DocumentThumbnail';
import PlayerTypeToggle from './PlayerTypeToggle';
import { matchesPlayerTypeValue } from '../utils/playerTypes';
import { currentUserCanUseProspectFilter } from '../utils/prospectUtils';

// -- Virtualised Autocomplete (pattern RechercheIntel)
const LISTBOX_PADDING = 8;
function renderRow(props) {
  const { data, index, style } = props;
  const item = data[index];
  return React.cloneElement(item, {
    style: { ...style, top: style.top + LISTBOX_PADDING },
  });
}
const OuterElementContext = React.createContext({});
const OuterElementType = React.forwardRef((props, ref) => {
  const outerProps = useContext(OuterElementContext);
  return (
    <div ref={ref} {...props} {...outerProps}
      style={{ ...props.style, overflowX: 'hidden', overflowY: 'scroll', maxWidth: '100%' }}
    />
  );
});
function useResetCache(data) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current != null) ref.current.resetAfterIndex(0, true); }, [data]);
  return ref;
}
const ListboxComponent = React.forwardRef((props, ref) => {
  const { children, ...other } = props;
  const itemData = React.Children.toArray(children);
  const itemCount = itemData.length;
  const itemSize = 48;
  const gridRef = useResetCache(itemCount);
  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={(itemCount > 8 ? 8 * itemSize : itemCount * itemSize) + 2 * LISTBOX_PADDING}
          width="100%" ref={gridRef} outerElementType={OuterElementType}
          innerElementType="ul" itemSize={() => itemSize} overscanCount={5}
          itemCount={itemCount} style={{ padding: 0, margin: 0, overflowX: 'hidden' }}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

// -- Utils
const getAvatarUrl = (userId) =>
  userId ? `${getMediaBaseUrl()}/users/${userId}.png?${new Date().getTime()}` : '';
const getPlayerAvatarUrl = (playerId) =>
  playerId ? `${getMediaBaseUrl()}/photos/${playerId}.png?${new Date().getTime()}` : '';
const getDocumentFileUrl = (filename) =>
  `${getMediaBaseUrl()}/documents/${filename}?${new Date().getTime()}`;

const getShortNationality = (nationality, t) => {
  if (!nationality) return '';
  const directMatch = nationality.match(/\((.*?)\)/);
  if (directMatch) return directMatch[1];
  const translated = t ? t(`createPlayer.nationalities.${nationality}`, { defaultValue: '' }) : '';
  if (translated) { const m = translated.match(/\((.*?)\)/); if (m) return m[1]; }
  return nationality;
};

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
const formatDate = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// -- Document thumbnail card (used in collapse)
const DocThumb = ({ doc, siblings, user, onEdit, onDelete, onPreview, t }) => {
  return (
    <Box
      sx={{
        border: '1px solid', borderColor: 'grey.200', borderRadius: 2,
        overflow: 'hidden', backgroundColor: 'white',
        transition: 'all 0.2s', '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.1)', transform: 'translateY(-1px)' },
      }}
    >
      {/* Preview zone */}
      <Box
        onClick={() => onPreview(doc, siblings)}
        sx={{
          height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', overflow: 'hidden', backgroundColor: 'grey.100',
        }}
      >
        <DocumentThumbnail doc={doc} iconSize={48} />
      </Box>

      {/* Info */}
      <Box sx={{ p: 1.5 }}>
        <Typography variant="body2" fontWeight={600} noWrap sx={{ mb: 0.5 }}>
          {doc.titre || doc.original_name}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <CalendarTodayIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">{formatDate(doc.created_at)}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
            • {formatFileSize(doc.file_size)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Avatar src={getAvatarUrl(doc.user_id)} sx={{ width: 18, height: 18 }} />
            <Typography variant="caption" color="text.secondary" noWrap>
              {doc.usuario_prenom} {doc.usuario_nom}
            </Typography>
          </Box>
          {canModifyRecord(user, doc.user_id) && (
            <Box sx={{ display: 'flex' }}>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(doc); }} sx={{ color: 'primary.main', p: 0.25 }}>
                <EditIcon sx={{ fontSize: 14 }} />
              </IconButton>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(doc); }} sx={{ color: 'error.main', p: 0.25 }}>
                <DeleteIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// -- Mobile player card
const MobilePlayerDocCard = ({ playerGroup, open, onToggle, user, t, translateFromDB, onEdit, onDelete, onPreview }) => {
  const theme = useTheme();
  const p = playerGroup;
  const shortNat = getShortNationality(p.player_nationalite, t);

  return (
    <Card sx={{ mb: 2, boxShadow: theme.shadows[2] }}>
      <CardHeader
        avatar={<Avatar src={getPlayerAvatarUrl(p.player_id)} alt={p.player_nom} />}
        title={
          user?.role?.toLowerCase() === 'entrenador' || user?.role?.toLowerCase() === 'pdp' ? (
            <Typography fontWeight={600}>{p.player_nom}</Typography>
          ) : (
            <Link to={`/players/${p.player_id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
              <Typography fontWeight={600}>{p.player_nom}</Typography>
            </Link>
          )
        }
        subheader={
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
            <Typography variant="caption">{translateFromDB(p.player_poste)}</Typography>
            <Typography variant="caption">• {p.player_annee_naissance}</Typography>
            {shortNat && <Typography variant="caption">• {shortNat}</Typography>}
          </Box>
        }
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip label={p.documents.length} size="small" color="primary" sx={{ fontWeight: 700, minWidth: 28 }} />
            <IconButton onClick={() => onToggle(p.player_id)}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </Box>
        }
      />
      <Collapse in={open} timeout="auto" unmountOnExit>
        <CardContent sx={{ pt: 0 }}>
          <Grid container spacing={1.5}>
            {p.documents.map((doc) => (
              <Grid item xs={6} key={doc.id}>
                <DocThumb doc={doc} siblings={p.documents} user={user} onEdit={onEdit} onDelete={onDelete} onPreview={onPreview} t={t} />
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Collapse>
    </Card>
  );
};


// -- Mobile filter drawer
const MobileFilterDrawer = ({
  open, onClose, players, users,
  selectedPlayer, selectedUser, onPlayerChange, onUserChange,
  selectedPlayerType, onPlayerTypeChange, isSuperAdmin, t,
}) => (
  <SwipeableDrawer
    anchor="bottom" open={open} onClose={onClose} onOpen={() => {}}
    sx={{ '& .MuiDrawer-paper': { borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: '80vh' } }}
  >
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">{t('rechercheDocuments.filters')}</Typography>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </Box>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Autocomplete options={players} getOptionLabel={(p) => p.nom} value={selectedPlayer}
            onChange={onPlayerChange} ListboxComponent={ListboxComponent}
            renderInput={(params) => <TextField {...params} label={t('rechercheDocuments.selectPlayer')} variant="outlined" />}
            renderOption={(props, p) => (
              <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar src={getPlayerAvatarUrl(p.id)} alt={p.nom} sx={{ mr: 1, width: 32, height: 32 }} />{p.nom}
              </Box>
            )}
            clearOnEscape
          />
        </Grid>
        <Grid item xs={12}>
          <Autocomplete options={users} getOptionLabel={(u) => `${u.prenom} ${u.nom}`} value={selectedUser}
            onChange={onUserChange} ListboxComponent={ListboxComponent}
            renderInput={(params) => <TextField {...params} label={t('rechercheDocuments.selectUser')} variant="outlined" />}
            renderOption={(props, u) => (
              <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar src={getAvatarUrl(u.id)} alt={u.nom} sx={{ mr: 1, width: 32, height: 32 }} />{u.prenom} {u.nom}
              </Box>
            )}
            clearOnEscape
          />
        </Grid>
        {isSuperAdmin && (
          <Grid item xs={12}>
            <PlayerTypeToggle value={selectedPlayerType} onChange={onPlayerTypeChange} t={t} tPrefix="rechercheDocuments" />
          </Grid>
        )}
      </Grid>
    </Box>
  </SwipeableDrawer>
);

// ====== COMPOSANT PRINCIPAL ======
const AllDocumentsTable = () => {
  const { t } = useTranslation();
  const { translateFromDB } = usePositions(t);

  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedPlayerType, setSelectedPlayerType] = useState('all');
  const [openRows, setOpenRows] = useState({});
  const toggleRow = (id) => setOpenRows((prev) => ({ ...prev, [id]: !prev[id] }));

  const [allDocsData, setAllDocsData] = useState([]);
  const [docsData, setDocsData] = useState([]);
  const [players, setPlayers] = useState([]);
  const [users, setUsers] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const [editDialog, setEditDialog] = useState({ open: false, doc: null, titre: '' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, doc: null });
  const [mediaPreview, setMediaPreview] = useState({ open: false, images: [], index: -1 });
  const [mediaDoc, setMediaDoc] = useState(null); // aperçu vidéo / html
  const [saving, setSaving] = useState(false);
  const [openFilterDrawer, setOpenFilterDrawer] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Fetch
  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);
        const localStoredUser = JSON.parse(localStorage.getItem('user'));
        const [resDocs, resUsers] = await Promise.all([
          api.get('/api/documents/all'),
          api.get('/api/users'),
        ]);
        let dataDocs = resDocs.data || [];
        const allUsers = Array.isArray(resUsers.data) ? resUsers.data : resUsers.data.users || [];

        let finalUser = localStoredUser;
        if (localStoredUser) {
          const found = allUsers.find(u => u.id === localStoredUser.id);
          if (found) finalUser = found;
        }
        setUser(finalUser);
        setUsers(allUsers);

        // Role filtering (same as RechercheIntel)
        if (finalUser) {
          const role = finalUser.role?.toLowerCase();
          if (role === 'entrenador' || role === 'pdp') {
            const uGroups = finalUser.groupes || [];
            dataDocs = dataDocs.filter((item) => {
              const isOwner = item.user_id === finalUser.id;
              const pGroups = item.player_groupes || [];
              return isOwner || pGroups.some((pg) => uGroups.some((ug) => ug.id === pg.id));
            });
          } else if (role === 'admin pro') {
            dataDocs = dataDocs.filter(i => i.player_type?.toLowerCase() === 'pro' || i.user_id === finalUser.id);
          } else if (role === 'admin cantera') {
            dataDocs = dataDocs.filter(i => i.player_type?.toLowerCase() === 'cantera' || i.user_id === finalUser.id);
          } else if (role === 'scout interne' || role === 'scout externe') {
            dataDocs = dataDocs.filter(i => i.user_id === finalUser.id);
          } else if (role === 'scout interne cantera' || role === 'scout externe cantera') {
            dataDocs = dataDocs.filter(i => i.player_type?.toLowerCase() === 'cantera' && i.user_id === finalUser.id);
          }
        }

        setAllDocsData(dataDocs);
        setDocsData(dataDocs);
      } catch (error) {
        console.error('Error fetching documents:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  useEffect(() => {
    const fetchPlayers = async () => {
      try {
        const res = await api.get('/api/players/select');
        setPlayers(Array.isArray(res.data) ? res.data : res.data.players || []);
      } catch (e) { setPlayers([]); }
    };
    fetchPlayers();
  }, []);

  // Filter
  useEffect(() => {
    let filtered = allDocsData;
    if (selectedPlayer) filtered = filtered.filter(d => d.player_id === selectedPlayer.id);
    if (selectedUser) filtered = filtered.filter(d => d.user_id === selectedUser.id);
    if (selectedPlayerType && selectedPlayerType !== 'all') {
      // Sous PRO, on écarte le vivier « NCAA + » — même gate que l'affichage du bucket.
      // Hissé HORS du callback : sinon localStorage serait relu à chaque ligne.
      const excludeProspects = currentUserCanUseProspectFilter();
      filtered = filtered.filter(d => matchesPlayerTypeValue(d, selectedPlayerType, { excludeProspects }));
    }
    setDocsData(filtered);
    setPage(0);
  }, [allDocsData, selectedPlayer, selectedUser, selectedPlayerType]);

  // Group by player
  const playerGroups = React.useMemo(() => {
    const map = {};
    docsData.forEach(doc => {
      if (!map[doc.player_id]) {
        map[doc.player_id] = {
          player_id: doc.player_id,
          player_nom: doc.player_nom,
          player_poste: doc.player_poste,
          player_type: doc.player_type,
          player_date_naissance: doc.player_date_naissance,
          player_nationalite: doc.player_nationalite,
          player_team: doc.player_team,
          player_league_1: doc.player_league_1,
          player_league_2: doc.player_league_2,
          player_annee_naissance: doc.player_annee_naissance,
          player_competitions_detail: doc.player_competitions_detail || [],
          documents: [],
        };
      }
      map[doc.player_id].documents.push(doc);
    });
    // Sort by most recent document
    return Object.values(map).sort((a, b) => {
      const dateA = new Date(a.documents[0]?.created_at || 0);
      const dateB = new Date(b.documents[0]?.created_at || 0);
      return dateB - dateA;
    });
  }, [docsData]);

  // Pagination on player groups
  const totalPlayers = playerGroups.length;
  const totalDocs = docsData.length;
  const paginatedGroups = React.useMemo(() => {
    if (rowsPerPage === -1) return playerGroups;
    return playerGroups.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [playerGroups, page, rowsPerPage]);

  // Actions
  const handleEditSave = async () => {
    if (!editDialog.doc) return;
    setSaving(true);
    try {
      await api.put(`/api/documents/${editDialog.doc.id}`, { titre: editDialog.titre });
      setAllDocsData(prev => prev.map(d => d.id === editDialog.doc.id ? { ...d, titre: editDialog.titre } : d));
      setEditDialog({ open: false, doc: null, titre: '' });
    } catch (err) {
      console.error('Error updating document:', err);
      alert(t('rechercheDocuments.editError'));
    } finally { setSaving(false); }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.doc) return;
    setSaving(true);
    try {
      await api.delete(`/api/documents/${deleteDialog.doc.id}`);
      setAllDocsData(prev => prev.filter(d => d.id !== deleteDialog.doc.id));
      setDeleteDialog({ open: false, doc: null });
    } catch (err) {
      console.error('Error deleting document:', err);
      alert(t('rechercheDocuments.deleteError'));
    } finally { setSaving(false); }
  };

  const handlePreview = (doc, siblings = []) => {
    if (isImage(doc.file_type)) {
      const images = (siblings.length ? siblings : [doc]).filter(d => isImage(d.file_type));
      const idx = images.findIndex(d => d.id === doc.id);
      setMediaPreview({ open: true, images, index: idx >= 0 ? idx : 0 });
    } else if (isVideo(doc.file_type) || isHtml(doc.file_type)) {
      setMediaDoc(doc);
    } else if (isPdf(doc.file_type)) {
      window.open(getDocumentFileUrl(doc.filename), '_blank');
    }
  };

  const handlePrevImage = React.useCallback(() => {
    setMediaPreview(prev => {
      if (prev.index < 0 || prev.images.length === 0) return prev;
      return { ...prev, index: (prev.index - 1 + prev.images.length) % prev.images.length };
    });
  }, []);

  const handleNextImage = React.useCallback(() => {
    setMediaPreview(prev => {
      if (prev.index < 0 || prev.images.length === 0) return prev;
      return { ...prev, index: (prev.index + 1) % prev.images.length };
    });
  }, []);

  useEffect(() => {
    if (!mediaPreview.open) return;
    const handleKey = (e) => {
      if (e.key === 'ArrowLeft') handlePrevImage();
      else if (e.key === 'ArrowRight') handleNextImage();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [mediaPreview.open, handlePrevImage, handleNextImage]);

  const currentPreviewImage = mediaPreview.index >= 0 ? mediaPreview.images[mediaPreview.index] : null;

  const onEdit = (doc) => setEditDialog({ open: true, doc, titre: doc.titre || '' });
  const onDelete = (doc) => setDeleteDialog({ open: true, doc });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <Typography variant="h6">{t('rechercheDocuments.loading')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', pb: isMobile ? 7 : 0 }}>
      {isMobile ? (
        <>
          {/* MOBILE */}
          <Box sx={{
            mx: 2, mt: 2, mb: 1, display: 'flex', alignItems: 'center', p: 2,
            bgcolor: theme.palette.primary.main, color: 'white', borderRadius: 1,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}>
            <Box sx={{
              bgcolor: 'rgba(255,255,255,0.2)', borderRadius: '50%',
              width: 40, height: 40, display: 'flex', alignItems: 'center',
              justifyContent: 'center', mr: 2, fontWeight: 'bold', fontSize: '1.1rem',
            }}>
              {totalDocs}
            </Box>
            <Typography variant="subtitle1">
              {totalDocs === 1 ? t('rechercheDocuments.documentFound') : t('rechercheDocuments.documentsFound')}
              {' '}({totalPlayers} {t('rechercheDocuments.players')})
            </Typography>
          </Box>

          <Box sx={{ p: 2 }}>
            {paginatedGroups.map((pg) => (
              <MobilePlayerDocCard key={pg.player_id} playerGroup={pg}
                open={openRows[pg.player_id] || false} onToggle={toggleRow}
                user={user} t={t} translateFromDB={translateFromDB}
                onEdit={onEdit} onDelete={onDelete} onPreview={handlePreview}
              />
            ))}
          </Box>

          {rowsPerPage !== -1 && totalPlayers > rowsPerPage && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <Pagination color="primary" count={Math.ceil(totalPlayers / rowsPerPage)}
                page={page + 1} onChange={(_, v) => setPage(v - 1)}
              />
            </Box>
          )}

          <Fab color="primary" sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: theme.zIndex.fab }}
            onClick={() => setOpenFilterDrawer(true)}>
            <FilterListIcon />
          </Fab>

          <MobileFilterDrawer
            open={openFilterDrawer} onClose={() => setOpenFilterDrawer(false)}
            players={players} users={users}
            selectedPlayer={selectedPlayer} selectedUser={selectedUser}
            onPlayerChange={(_, v) => setSelectedPlayer(v)}
            onUserChange={(_, v) => setSelectedUser(v)}
            selectedPlayerType={selectedPlayerType}
            onPlayerTypeChange={(val) => setSelectedPlayerType(val)}
            isSuperAdmin={user?.role?.toLowerCase() === 'super admin'}
            t={t}
          />
        </>
      ) : (
        <>
          {/* DESKTOP */}
          <Grid container spacing={2} marginBottom={2}>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <Autocomplete options={players} getOptionLabel={(p) => p.nom}
                  value={selectedPlayer} onChange={(_, v) => setSelectedPlayer(v)}
                  ListboxComponent={ListboxComponent}
                  renderInput={(params) => <TextField {...params} label={t('rechercheDocuments.selectPlayer')} variant="outlined" />}
                  renderOption={(props, p) => (
                    <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar src={getPlayerAvatarUrl(p.id)} alt={p.nom} sx={{ mr: 1 }} />{p.nom}
                    </Box>
                  )}
                  clearOnEscape
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <Autocomplete options={users} getOptionLabel={(u) => `${u.prenom} ${u.nom}`}
                  value={selectedUser} onChange={(_, v) => setSelectedUser(v)}
                  ListboxComponent={ListboxComponent}
                  renderInput={(params) => <TextField {...params} label={t('rechercheDocuments.selectUser')} variant="outlined" />}
                  renderOption={(props, u) => (
                    <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar src={getAvatarUrl(u.id)} alt={u.nom} sx={{ width: 30, height: 30, mr: 1 }} />{u.prenom} {u.nom}
                    </Box>
                  )}
                  clearOnEscape
                />
              </FormControl>
            </Grid>
            {user?.role?.toLowerCase() === 'super admin' && (
              <Grid item xs={12} sm={3}>
                <PlayerTypeToggle
                  value={selectedPlayerType}
                  onChange={(val) => setSelectedPlayerType(val)}
                  t={t}
                  tPrefix="rechercheDocuments"
                />
              </Grid>
            )}
          </Grid>

          <Box sx={{
            mb: 3, display: 'flex', alignItems: 'center', p: 2,
            bgcolor: theme.palette.primary.main, color: 'white', borderRadius: 1,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}>
            <Box sx={{
              bgcolor: 'rgba(255,255,255,0.2)', borderRadius: '50%',
              width: 48, height: 48, display: 'flex', alignItems: 'center',
              justifyContent: 'center', mr: 2, fontWeight: 'bold', fontSize: '1.2rem',
            }}>
              {totalDocs}
            </Box>
            <Typography variant="h6">
              {totalDocs === 1 ? t('rechercheDocuments.documentFound') : t('rechercheDocuments.documentsFound')}
              {' '}({totalPlayers} {t('rechercheDocuments.players')})
            </Typography>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width={50} />
                  <TableCell>{t('rechercheDocuments.player')}</TableCell>
                  <TableCell>{t('rechercheDocuments.position')}</TableCell>
                  <TableCell>{t('rechercheDocuments.year')}</TableCell>
                  <TableCell>{t('rechercheDocuments.nationality')}</TableCell>
                  <TableCell>{t('rechercheDocuments.team')}</TableCell>
                  <TableCell align="center">{t('rechercheDocuments.document')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedGroups.map((pg) => {
                  const shortNat = getShortNationality(pg.player_nationalite, t);
                  return (
                    <React.Fragment key={pg.player_id}>
                      <TableRow
                        hover sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' } }}
                        onClick={(e) => {
                          if (e.target.closest('a') || e.target.closest('button')) return;
                          toggleRow(pg.player_id);
                        }}
                      >
                        <TableCell>
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleRow(pg.player_id); }}>
                            {openRows[pg.player_id] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar src={getPlayerAvatarUrl(pg.player_id)} alt={pg.player_nom} sx={{ mr: 1.5, width: 36, height: 36 }} />
                            {user?.role?.toLowerCase() === 'entrenador' || user?.role?.toLowerCase() === 'pdp' ? (
                              <Typography variant="body2" fontWeight={600}>{pg.player_nom}</Typography>
                            ) : (
                              <Link to={`/players/${pg.player_id}`}
                                style={{ textDecoration: 'none', color: 'inherit' }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Typography variant="body2" fontWeight={600}>{pg.player_nom}</Typography>
                              </Link>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>{translateFromDB(pg.player_poste)}</TableCell>
                        <TableCell>{pg.player_annee_naissance}</TableCell>
                        <TableCell>{shortNat}</TableCell>
                        <TableCell>
                          <Box>
                            {pg.player_competitions_detail && pg.player_competitions_detail.length > 0 ? (
                              <>
                                {[...new Set(pg.player_competitions_detail.map(c => c.equipe_nom))].map((equipe, idx) => {
                                  const eqData = pg.player_competitions_detail.find(c => c.equipe_nom === equipe);
                                  const comps = pg.player_competitions_detail
                                    .filter(c => c.equipe_nom === equipe)
                                    .filter((v, i, s) => i === s.findIndex(c => c.competition_id === v.competition_id));
                                  const mUrl = getMediaBaseUrl();
                                  const ts = Date.now();
                                  return (
                                    <Box key={idx} sx={{ mb: 0.5 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Box component="img" src={`${mUrl}/equipes/${eqData.equipe_id}.jpg?t=${ts}`}
                                          alt={equipe}
                                          sx={{ width: 16, height: 16, objectFit: 'contain', borderRadius: '2px' }}
                                          onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                        <div style={{ fontWeight: 'bold', fontSize: '12px' }}>{equipe}</div>
                                      </Box>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, flexWrap: 'wrap', mt: 0.3 }}>
                                        {comps.map((c, ci) => (
                                          <Box key={ci} sx={{ display: 'flex', alignItems: 'center', gap: 0.2 }}>
                                            <Box component="img" src={`${mUrl}/competitions/${c.competition_id}.jpg?t=${ts}`}
                                              alt={c.competition_nom}
                                              sx={{ width: 14, height: 14, objectFit: 'contain', borderRadius: '2px' }}
                                              onError={(e) => { e.target.style.display = 'none'; }}
                                            />
                                            <span style={{ fontSize: '10px' }}>{c.competition_nom}</span>
                                          </Box>
                                        ))}
                                      </Box>
                                    </Box>
                                  );
                                })}
                              </>
                            ) : (
                              <Box>
                                {pg.player_team && <div style={{ fontSize: '12px' }}>{pg.player_team}</div>}
                                {(pg.player_league_1 || pg.player_league_2) && (
                                  <div style={{ fontSize: '10px', color: '#666' }}>
                                    {[pg.player_league_1, pg.player_league_2].filter(Boolean).join(' - ')}
                                  </div>
                                )}
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={pg.documents.length} color="primary" size="small" sx={{ fontWeight: 700, minWidth: 32 }} />
                        </TableCell>
                      </TableRow>

                      {/* Collapse: document grid */}
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                          <Collapse in={openRows[pg.player_id]} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1, my: 1 }}>
                              <Grid container spacing={2}>
                                {pg.documents.map((doc) => (
                                  <Grid item xs={6} sm={4} md={3} lg={2} key={doc.id}>
                                    <DocThumb doc={doc} siblings={pg.documents} user={user} onEdit={onEdit} onDelete={onDelete} onPreview={handlePreview} t={t} />
                                  </Grid>
                                ))}
                              </Grid>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>

            <TablePagination
              rowsPerPageOptions={[10, 25, 50, { label: t('rechercheDocuments.all'), value: -1 }]}
              component="div" count={totalPlayers} rowsPerPage={rowsPerPage} page={page}
              onPageChange={(_, p) => setPage(p)}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            />
          </TableContainer>
        </>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, doc: null, titre: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>{t('rechercheDocuments.editTitle')}</DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" label={t('rechercheDocuments.titleLabel')} fullWidth
            value={editDialog.titre}
            onChange={(e) => setEditDialog(prev => ({ ...prev, titre: e.target.value }))}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, doc: null, titre: '' })} disabled={saving}>
            {t('rechercheDocuments.cancel')}
          </Button>
          <Button onClick={handleEditSave} variant="contained" disabled={saving}>
            {saving ? t('rechercheDocuments.saving') : t('rechercheDocuments.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, doc: null })}>
        <DialogTitle>{t('rechercheDocuments.deleteConfirm')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('rechercheDocuments.deleteMessage', { name: deleteDialog.doc?.titre || deleteDialog.doc?.original_name })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, doc: null })} disabled={saving}>
            {t('rechercheDocuments.cancel')}
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={saving}>
            {saving ? t('rechercheDocuments.deleting') : t('rechercheDocuments.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog open={mediaPreview.open} onClose={() => setMediaPreview({ open: false, images: [], index: -1 })} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ pl: 1 }}>
            {currentPreviewImage && (currentPreviewImage.titre || currentPreviewImage.original_name)}
            {mediaPreview.images.length > 1 && ` (${mediaPreview.index + 1}/${mediaPreview.images.length})`}
          </Typography>
          <IconButton onClick={() => setMediaPreview({ open: false, images: [], index: -1 })}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, position: 'relative' }}>
          {mediaPreview.images.length > 1 && (
            <IconButton onClick={handlePrevImage}
              sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
                backgroundColor: 'rgba(0,0,0,0.5)', color: 'white',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' }, zIndex: 1 }}>
              <ChevronLeftIcon />
            </IconButton>
          )}
          {currentPreviewImage && (
            <Box component="img" src={getDocumentFileUrl(currentPreviewImage.filename)} alt="Preview"
              sx={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain', borderRadius: 1 }}
            />
          )}
          {mediaPreview.images.length > 1 && (
            <IconButton onClick={handleNextImage}
              sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                backgroundColor: 'rgba(0,0,0,0.5)', color: 'white',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' }, zIndex: 1 }}>
              <ChevronRightIcon />
            </IconButton>
          )}
        </DialogContent>
      </Dialog>

      {/* Video / HTML Preview Dialog */}
      <DocumentMediaDialog
        open={!!mediaDoc}
        doc={mediaDoc}
        fileUrl={mediaDoc ? getDocumentFileUrl(mediaDoc.filename) : null}
        onClose={() => setMediaDoc(null)}
      />
    </Box>
  );
};

export default AllDocumentsTable;
